import Handlebars from 'handlebars';

import {
  AGGREGATION_TYPES,
  type AggregationType,
  COMPARISON_OPERATORS,
  FormatType,
  type TEMPLATE_SPECIAL_FORMAT_KEYS,
  TEXT_TEMPLATE_FORMAT_TYPES,
  type TextTemplateFormatKey,
} from '@revelio/iso-utility';
import {
  DECIMAL_PRECISION_REGEX,
  getFormatter,
  PERCENTILE_DECIMAL_REGEX,
} from '@revelio/replots';

import { type Filter, FilterId } from '../../../../filters/types/filters';
import { isSafeFieldName } from '../../../../utils/validation.utils';
import type { MultiMetricDataMap } from '../../../DataProvider/MultiMetricProvider';
import {
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
} from '../../../DataProvider/types';
import { metricToTitleMap } from '../../../PageConfigProvider/constants';
import { compositionSecondaryDimensionSet } from '../../../PageConfigProvider/types/dimension';
import {
  type MetricValues,
  metricOptions,
} from '../../../PageConfigProvider/types/metric';
import { getHelperDefinition, HELPER_REGISTRY } from '../helper-registry';
import { buildErrorToken, buildNoDataToken } from './diagnostic-tokens';
import type { ErrorCollector, HelperError } from './error-collector';
import {
  type ExtractionOptions,
  extractDatasetValues,
} from './extractDatasetValues';
import { evaluateMathExpression } from './mathExpression';
import {
  type AggregationFunction,
  getArgMax,
  getArgMin,
  getCompositePercentileRank,
  getMaxPoint,
  getMaxPointEntity,
  getMinPoint,
  getMinPointEntity,
  getRank,
  getSortedEntityValues,
  isValidAggregationFunction,
  reduceNumericValues,
} from './reporting-logic';
import {
  applyCompanyRcidToExtractionOptions,
  formatFilterValue,
  resolveCompanyRcidFromFilters,
  resolveExcludeEntityInOptions,
} from './resolve-entity-utils';

export function balanceQuotes(content: string): string {
  return content.replace(/\{\{([^}]+)\}\}/g, (match, inner) => {
    const unescaped = inner.replace(/\\"/g, '');
    const quoteCount = (unescaped.match(/"/g) || []).length;

    if (quoteCount % 2 !== 0) {
      return match.replace(/\}\}$/, '"}}');
    }
    return match;
  });
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const formatDateString = (s: string): string => {
  const trimmed = String(s).trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
  if (!match || match[1] == null || match[2] == null) return trimmed;
  const year = match[1];
  const monthIndex = parseInt(match[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return trimmed;
  return `${MONTH_NAMES[monthIndex]} ${year}`;
};

/**
 * Formats a number as an ordinal (1st, 2nd, 3rd, 4th, etc.).
 * Used for rank display, e.g. {{FORMAT (RANK "overall" (filter "COMPANY")) format="ORDINAL"}}.
 */
const formatOrdinal = (n: number): string => {
  const integer = Math.round(n);
  if (!Number.isFinite(integer)) return '';
  const mod100 = integer % 100;
  const mod10 = integer % 10;
  let suffix: string;
  if (mod100 >= 11 && mod100 <= 13) {
    suffix = 'th';
  } else if (mod10 === 1) {
    suffix = 'st';
  } else if (mod10 === 2) {
    suffix = 'nd';
  } else if (mod10 === 3) {
    suffix = 'rd';
  } else {
    suffix = 'th';
  }
  return `${integer}${suffix}`;
};

/** Handlers for template-only format keys (ORDINAL, DATE). Key must be in TEMPLATE_SPECIAL_FORMAT_KEYS. */
const TEMPLATE_FORMAT_HANDLERS: Record<
  (typeof TEMPLATE_SPECIAL_FORMAT_KEYS)[number],
  (value: number | string) => string
> = {
  ORDINAL: (value) =>
    typeof value === 'number'
      ? formatOrdinal(value)
      : formatOrdinal(Number(value)) || '',
  DATE: (value) =>
    formatDateString(typeof value === 'string' ? value : String(value)),
};

/**
 * Resolves a format string to a FormatType.
 * Accepts both enum keys (e.g., "SI", "PERCENTAGE") and enum values (e.g., "s", "%").
 */
const resolveFormatType = (format: string): FormatType | null => {
  // Check if it's a key (e.g., "SI", "PERCENTAGE")
  // TEXT_TEMPLATE_FORMAT_TYPES values are guaranteed to be FormatType since they're derived from it
  if (format in TEXT_TEMPLATE_FORMAT_TYPES) {
    return TEXT_TEMPLATE_FORMAT_TYPES[format as TextTemplateFormatKey];
  }
  // Check if it's a value directly (e.g., "s", "%")
  const values = Object.values(FormatType);
  if (values.includes(format as FormatType)) {
    return format as FormatType;
  }
  return null;
};

export type DataContext = {
  filters: Filter[];
  parameterFilters?: Filter[];
  multiData?: MultiMetricDataMap;
  currentMetric?: MetricValues;
  segment?: string;
};

type AstLocation = {
  start: { line: number; column: number };
  end: { line: number; column: number };
};

type AstPath = {
  original?: string;
};

type AstHashPair = {
  key?: string;
  value?: AstNode;
};

type AstHash = {
  pairs?: AstHashPair[];
};

type AstNode = {
  type?: string;
  loc?: AstLocation;
  body?: AstNode[];
  path?: AstPath;
  params?: AstNode[];
  hash?: AstHash;
  program?: AstNode;
  inverse?: AstNode;
  value?: string;
};

const BUILT_IN_HANDLEBARS_HELPERS = new Set([
  'if',
  'unless',
  'each',
  'with',
  'lookup',
  'log',
  'helperMissing',
  'blockHelperMissing',
]);

const CUSTOM_NON_REGISTRY_HELPERS = new Set(COMPARISON_OPERATORS);
const VALID_FILTER_IDS = new Set<string>(Object.values(FilterId));
const COMMON_EXTRACTION_HASH_PARAMS = new Set([
  'percentile',
  'field',
  'segment',
  'entity',
  'label',
  'date',
  'whereField',
  'equals',
  'labelField',
  'flattenNested',
  'timeAggregation',
  'excludeEntity',
  'property',
]);
const METRIC_HASH_PARAMS_NOT_FOR_EXTRACTION = new Set(['secondaryDimension']);
const HELPERS_WITH_REDUCE = new Set([
  'value',
  'arg_max',
  'arg_min',
  'rank',
  'share',
  'table',
  'percentile_rank',
]);

const isDatasetEmpty = (dataset: DataProviderSet): boolean => {
  switch (dataset.type) {
    case DataType.MAP:
      return dataset.points.length === 0;
    case DataType.TIMESERIES:
      return dataset.data.every((series) => series.timeseries.length === 0);
    case DataType.BREAKDOWN_SINGLE:
    case DataType.BREAKDOWN_TIMESERIES:
      return dataset.data.every((group) => group.breakdown.length === 0);
    case DataType.BOX:
      if (isBoxBreakdownDataSet(dataset)) {
        return dataset.data.every((group) => group.breakdown.length === 0);
      }
      return dataset.data.length === 0;
    default:
      return dataset.data.length === 0;
  }
};

const toErrorPosition = (
  loc: AstLocation | undefined
): HelperError['position'] | undefined => {
  if (!loc) return undefined;
  return {
    line: loc.start.line,
    column: loc.start.column,
    endLine: loc.end.line,
    endColumn: loc.end.column,
  };
};

const isStringLiteralNode = (node: AstNode | undefined): node is AstNode => {
  return node?.type === 'StringLiteral' && typeof node.value === 'string';
};

const getHelperName = (node: AstNode): string | null => {
  const name = node.path?.original;
  return typeof name === 'string' && name.trim().length > 0 ? name : null;
};

const hasHashParam = (node: AstNode, key: string): boolean => {
  const pairs = node.hash?.pairs ?? [];
  return pairs.some(
    (pair) =>
      typeof pair.key === 'string' &&
      pair.key.toLowerCase() === key.toLowerCase() &&
      pair.value !== undefined
  );
};

const getHashValue = (node: AstNode, key: string): AstNode | undefined => {
  const pairs = node.hash?.pairs ?? [];
  return pairs.find((pair) => pair.key?.toLowerCase() === key.toLowerCase())
    ?.value;
};

const getAllowedHashParams = (helperName: string): Set<string> | null => {
  const name = helperName.toLowerCase();

  if (name === 'math') return null;

  if (name === 'format') {
    return new Set(['format']);
  }

  if (name === 'max_point' || name === 'min_point') {
    return new Set([
      ...COMMON_EXTRACTION_HASH_PARAMS,
      ...METRIC_HASH_PARAMS_NOT_FOR_EXTRACTION,
      'property',
    ]);
  }

  if (name === 'table') {
    return new Set([
      ...COMMON_EXTRACTION_HASH_PARAMS,
      ...METRIC_HASH_PARAMS_NOT_FOR_EXTRACTION,
      'format',
      'limit',
      'reduce',
    ]);
  }

  if (
    name === 'filter' ||
    name === 'param' ||
    name === 'has_param' ||
    name === 'metric'
  ) {
    return new Set();
  }

  if (HELPERS_WITH_REDUCE.has(name)) {
    return new Set([
      ...COMMON_EXTRACTION_HASH_PARAMS,
      ...METRIC_HASH_PARAMS_NOT_FOR_EXTRACTION,
      'reduce',
    ]);
  }

  if (
    name === 'has_data' ||
    name === 'sum' ||
    name === 'avg' ||
    name === 'min' ||
    name === 'max' ||
    name === 'count'
  ) {
    return new Set([
      ...COMMON_EXTRACTION_HASH_PARAMS,
      ...METRIC_HASH_PARAMS_NOT_FOR_EXTRACTION,
    ]);
  }

  return null;
};

const validateHelperCall = (
  node: AstNode,
  dataContext: DataContext,
  issues: HelperError[]
) => {
  const helperName = getHelperName(node);
  if (!helperName) return;

  const definition = getHelperDefinition(helperName.toLowerCase());
  if (!definition) {
    const normalizedName = helperName.toLowerCase();
    if (
      !BUILT_IN_HANDLEBARS_HELPERS.has(normalizedName) &&
      !CUSTOM_NON_REGISTRY_HELPERS.has(
        normalizedName as (typeof COMPARISON_OPERATORS)[number]
      )
    ) {
      issues.push({
        helperName,
        message: `Helper "${helperName}" does not exist`,
        severity: 'error',
        position: toErrorPosition(node.loc),
      });
    }
    return;
  }

  if (definition.parameterType === 'metric') {
    const metricArg = node.params?.[0];
    if (isStringLiteralNode(metricArg)) {
      const rawMetricKey = metricArg.value?.trim() ?? '';
      if (rawMetricKey.length === 0) return;

      const canonicalMetric = metricOptions.find(
        (opt) => opt.value.toLowerCase() === rawMetricKey.toLowerCase()
      )?.value;

      if (canonicalMetric === undefined) {
        issues.push({
          helperName: definition.name,
          message: `Metric "${rawMetricKey}" not found`,
          severity: 'error',
          position: toErrorPosition(metricArg.loc),
        });
        return;
      }

      const secondaryDimNode = getHashValue(node, 'secondaryDimension');
      if (
        secondaryDimNode &&
        isStringLiteralNode(secondaryDimNode) &&
        typeof secondaryDimNode.value === 'string'
      ) {
        const dim = secondaryDimNode.value.trim();
        if (dim.length > 0 && !compositionSecondaryDimensionSet.has(dim)) {
          issues.push({
            helperName: definition.name,
            message: `Invalid secondaryDimension "${dim}"`,
            severity: 'error',
            position: toErrorPosition(secondaryDimNode.loc),
          });
        }
      }

      const multiData = dataContext.multiData;
      if (!multiData || Object.keys(multiData).length === 0) {
        return;
      }

      const resolvedDataKey = canonicalMetric ?? rawMetricKey;

      const dataset = multiData[resolvedDataKey];
      if (!dataset || isDatasetEmpty(dataset)) {
        issues.push({
          helperName: definition.name,
          message: 'No data available for this metric with current filters',
          severity: 'warning',
          position: toErrorPosition(metricArg.loc),
        });
      }
    }
  }

  const allowedHashParams = getAllowedHashParams(definition.name);
  if (allowedHashParams) {
    (node.hash?.pairs ?? []).forEach((pair) => {
      const key = pair.key?.trim();
      if (!key) return;
      if (!allowedHashParams.has(key)) {
        issues.push({
          helperName: definition.name,
          message: `Unknown hash param "${key}" for ${definition.name}`,
          severity: 'warning',
          position: toErrorPosition(pair.value?.loc ?? node.loc),
        });
      }
    });
  }

  if (HELPERS_WITH_REDUCE.has(definition.name.toLowerCase())) {
    const reduceValue = getHashValue(node, 'reduce');
    if (reduceValue && isStringLiteralNode(reduceValue)) {
      const reduce = reduceValue.value?.trim() ?? '';
      if (reduce.length > 0 && !isValidAggregationFunction(reduce)) {
        issues.push({
          helperName: definition.name,
          message: `Invalid reduce value "${reduce}"`,
          severity: 'error',
          position: toErrorPosition(reduceValue.loc),
        });
      }
    }
  }

  if (definition.name.toLowerCase() === 'rank') {
    const hasEntityPositional = Boolean(node.params && node.params.length >= 2);
    const hasEntityHash = hasHashParam(node, 'entity');
    if (!hasEntityPositional && !hasEntityHash) {
      issues.push({
        helperName: definition.name,
        message: 'RANK requires an entity parameter',
        severity: 'error',
        position: toErrorPosition(node.loc),
      });
    }
  }

  if (definition.name.toLowerCase() === 'filter') {
    const filterArg = node.params?.[0];
    if (!isStringLiteralNode(filterArg)) {
      issues.push({
        helperName: definition.name,
        message: 'FILTER requires a FilterId string as first argument',
        severity: 'error',
        position: toErrorPosition(node.loc),
      });
      return;
    }
    if (isStringLiteralNode(filterArg)) {
      const filterId = filterArg.value?.trim() ?? '';
      if (filterId.length > 0 && !VALID_FILTER_IDS.has(filterId)) {
        issues.push({
          helperName: definition.name,
          message: `Invalid filter id "${filterId}". Use a valid FilterId value.`,
          severity: 'error',
          position: toErrorPosition(filterArg.loc),
        });
      }
    }
  }

  if (definition.name.toLowerCase() === 'param') {
    const paramNameArg = node.params?.[0];
    if (!isStringLiteralNode(paramNameArg)) {
      issues.push({
        helperName: definition.name,
        message: 'PARAM requires a parameter name string as its only argument',
        severity: 'error',
        position: toErrorPosition(node.loc),
      });
      return;
    }
    if (node.params?.[1]) {
      issues.push({
        helperName: definition.name,
        message:
          'PARAM no longer accepts a second (index) argument. Use {{PARAM "Company"}} instead of {{PARAM "COMPANY" 0}}.',
        severity: 'error',
        position: toErrorPosition(node.params[1].loc),
      });
    }
  }

  if (definition.name.toLowerCase() === 'has_param') {
    const paramNameArg = node.params?.[0];
    if (!isStringLiteralNode(paramNameArg)) {
      issues.push({
        helperName: definition.name,
        message:
          'HAS_PARAM requires a parameter name string as its only argument',
        severity: 'error',
        position: toErrorPosition(node.loc),
      });
      return;
    }
    if (node.params?.[1]) {
      issues.push({
        helperName: definition.name,
        message:
          'HAS_PARAM accepts only one argument. Use {{HAS_PARAM "Occupation"}}.',
        severity: 'error',
        position: toErrorPosition(node.params[1].loc),
      });
    }
  }
};

const traverseAst = (
  node: AstNode | undefined,
  dataContext: DataContext,
  issues: HelperError[]
) => {
  if (!node?.type) return;

  if (
    node.type === 'MustacheStatement' ||
    node.type === 'SubExpression' ||
    node.type === 'BlockStatement'
  ) {
    validateHelperCall(node, dataContext, issues);
  }

  if (node.program) traverseAst(node.program, dataContext, issues);
  if (node.inverse) traverseAst(node.inverse, dataContext, issues);

  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  (node.body ?? []).forEach((child) => traverseAst(child, dataContext, issues));
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  (node.params ?? []).forEach((child) =>
    traverseAst(child, dataContext, issues)
  );
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  (node.hash?.pairs ?? []).forEach((pair) =>
    traverseAst(pair.value, dataContext, issues)
  );
};

export const validateHandlebarsTemplate = (
  content: string,
  dataContext: DataContext
): HelperError[] => {
  try {
    const safeContent = balanceQuotes(content);
    const ast = Handlebars.parse(safeContent) as AstNode;
    const issues: HelperError[] = [];
    traverseAst(ast, dataContext, issues);
    return issues;
  } catch (error) {
    const errorWithLoc = error as {
      message?: string;
      hash?: { loc?: { first_line?: number; first_column?: number } };
    };
    return [
      {
        helperName: 'COMPILATION',
        message: errorWithLoc.message ?? 'Template syntax error',
        severity: 'error',
        position:
          errorWithLoc.hash?.loc?.first_line !== undefined &&
          errorWithLoc.hash?.loc?.first_column !== undefined
            ? {
                line: errorWithLoc.hash.loc.first_line,
                column: errorWithLoc.hash.loc.first_column,
              }
            : undefined,
      },
    ];
  }
};

const computeAggregationFromDataset = (
  dataset: DataProviderSet,
  extractionOptions: ExtractionOptions | undefined,
  aggregationType: AggregationType
): number | null => {
  const values = extractDatasetValues(dataset, extractionOptions);
  const numericValues = values.filter(
    (v): v is number => v !== null && Number.isFinite(v)
  );
  if (numericValues.length === 0) return null;
  switch (aggregationType) {
    case 'SUM':
      return numericValues.reduce((acc, v) => acc + v, 0);
    case 'AVG':
      return (
        numericValues.reduce((acc, v) => acc + v, 0) / numericValues.length
      );
    case 'MIN':
      return Math.min(...numericValues);
    case 'MAX':
      return Math.max(...numericValues);
    case 'COUNT':
      return numericValues.length;
    default:
      return null;
  }
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isValidPercentile = (
  value: unknown
): value is NonNullable<ExtractionOptions['percentile']> =>
  value === 'q10' || value === 'q50' || value === 'q90';

const isValidTimeAggregation = (
  value: unknown
): value is NonNullable<ExtractionOptions['timeAggregation']> =>
  value === 'all' || value === 'latest' || value === 'first';

const isValidBreakdownProperty = (
  v: unknown
): v is NonNullable<ExtractionOptions['property']> =>
  v === 'value' || v === 'share';

const VALIDATORS = {
  percentile: isValidPercentile,
  timeAggregation: isValidTimeAggregation,
  flattenNested: (v: unknown) => typeof v === 'boolean',
  field: (v: unknown) => isString(v) && isSafeFieldName(v),
  segment: (v: unknown) => isString(v) && v.length > 0,
  entity: (v: unknown) => isString(v) && v.length > 0,
  label: (v: unknown) => isString(v) && v.length > 0,
  date: (v: unknown) => isString(v) && v.length > 0,
  whereField: (v: unknown) => isString(v) && isSafeFieldName(v),
  equals: (v: unknown) => isString(v) && v.length > 0,
  labelField: (v: unknown) => isString(v) && isSafeFieldName(v),
  excludeEntity: (v: unknown) => isString(v) && v.length > 0,
  property: isValidBreakdownProperty,
} as const;

const DEFAULT_OPTIONS: Required<
  Pick<ExtractionOptions, 'timeAggregation' | 'flattenNested'>
> = {
  timeAggregation: 'all',
  flattenNested: true,
};

/**
 * Normalizes a hash value so that Handlebars SafeString (e.g. from entity=(ARG_MAX "headcount"))
 * is coerced to a plain string. Otherwise options like entity would fail isString() and be dropped.
 * Only stringifies object types with toString to avoid converting numbers (e.g. entity id).
 */
const normalizeHashValue = (value: unknown): unknown => {
  if (value === null || value === undefined || typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'object') {
    const safeStringCandidate = value as {
      toHTML?: unknown;
      toString?: unknown;
    };
    if (
      'toHTML' in safeStringCandidate &&
      typeof safeStringCandidate.toString === 'function'
    ) {
      return safeStringCandidate.toString();
    }
  }
  return value;
};

/**
 * Builds extraction options from Handlebars helper hash.
 */
const buildExtractionOptions = (
  options: Handlebars.HelperOptions | undefined
): ExtractionOptions => {
  const hash = getHelperHash(options);

  const validatedOptions: Record<string, unknown> = {};
  Object.keys(VALIDATORS).forEach((key) => {
    const validator = VALIDATORS[key as keyof typeof VALIDATORS];
    const rawValue = normalizeHashValue(hash[key]);

    if (validator(rawValue)) {
      validatedOptions[key] = rawValue;
    }
  });

  return {
    ...DEFAULT_OPTIONS,
    ...validatedOptions,
  } as ExtractionOptions;
};

const getHelperHash = (
  options: Handlebars.HelperOptions | undefined
): Record<string, unknown> => options?.hash ?? {};

const getReduceOption = (
  hash: Record<string, unknown>,
  defaultReduce: AggregationFunction
): AggregationFunction =>
  isValidAggregationFunction(hash.reduce) ? hash.reduce : defaultReduce;

const POINT_PROPERTIES = ['x', 'y', 'date', 'value', 'entity'] as const;
type PointProperty = (typeof POINT_PROPERTIES)[number];

const getPointProperty = (
  hash: Record<string, unknown>,
  defaultProp: PointProperty = 'value'
): PointProperty =>
  POINT_PROPERTIES.includes(hash.property as PointProperty)
    ? (hash.property as PointProperty)
    : defaultProp;

/** Merges positional entity (args[1]) into options.hash for helpers that accept entity as 2nd arg. */
const mergeEntityIntoOptions = (
  args: unknown[],
  options: Handlebars.HelperOptions
): Handlebars.HelperOptions => {
  const entityParam = args.length >= 3 ? args[1] : options.hash?.entity;
  if (entityParam == null) return options;
  return {
    ...options,
    hash: { ...options.hash, entity: entityParam },
  };
};

const defaultIsEmpty = <T>(r: T | null): boolean =>
  r === null || (typeof r === 'number' && Number.isNaN(r)) || r === '';

export type ComputationFn<T> = (
  dataset: DataProviderSet,
  options: ExtractionOptions,
  helperOptions: Handlebars.HelperOptions
) => T | null;

export type CreateMetricHelperOptions<T> = {
  defaultValue?: T | string;
  isEmpty?: (result: T | null) => boolean;
  /** When set, a warning is reported when the helper returns empty (e.g. no data). */
  helperName?: string;
  diagnosticsMode?: boolean;
  diagnosticsVerbose?: boolean;
};

export type WrapHelperOutputFn = (
  value: string | Handlebars.SafeString
) => Handlebars.SafeString;

export const createMetricHelper = <T>(
  dataContext: DataContext,
  computationFn: ComputationFn<T>,
  options?: CreateMetricHelperOptions<T>,
  errorCollector?: ErrorCollector,
  hbsInstance?: typeof Handlebars,
  wrapHelperOutput?: WrapHelperOutputFn
): ((
  metricParam: unknown,
  options: Handlebars.HelperOptions
) => T | string | Handlebars.SafeString) => {
  const defaultValue = (options?.defaultValue ?? '') as T | string;
  const isEmpty = options?.isEmpty ?? defaultIsEmpty;
  const metricHelperName = options?.helperName ?? 'METRIC_HELPER';
  const diagnosticsMode = options?.diagnosticsMode ?? false;
  const diagnosticsVerbose = options?.diagnosticsVerbose ?? false;

  const makeEmptyToken = (
    invocation: string
  ): Handlebars.SafeString | string => {
    if (!diagnosticsMode) {
      return hbsInstance ? new hbsInstance.SafeString(invocation) : invocation;
    }
    if (!hbsInstance) return invocation;
    const detail = diagnosticsVerbose ? invocation : undefined;
    return new hbsInstance.SafeString(
      buildNoDataToken(metricHelperName, detail)
    );
  };

  const makeErrorToken = (
    errorType: string,
    detail?: string
  ): Handlebars.SafeString | string => {
    if (!diagnosticsMode || !hbsInstance) return '';
    return new hbsInstance.SafeString(
      buildErrorToken(errorType, metricHelperName, detail)
    );
  };

  const emptyReturn = (
    metricParam: unknown,
    opts: Handlebars.HelperOptions
  ): Handlebars.SafeString | string => {
    const serialized = serializeHelperInvocation(
      metricHelperName,
      [metricParam],
      opts.hash
    );
    const value = makeEmptyToken(serialized);
    return wrapHelperOutput ? wrapHelperOutput(value) : value;
  };

  return (metricParam: unknown, options: Handlebars.HelperOptions) => {
    if (!isString(metricParam)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: metricHelperName,
          message: 'First argument must be a metric name (string)',
          suggestion: 'Use a metric key in quotes, e.g. "headcount"',
          args: [metricParam],
        });
      }
      if (diagnosticsMode) {
        return makeErrorToken('Invalid arguments');
      }
      return emptyReturn(metricParam, options);
    }

    const dataKey = metricParam as MetricValues;
    const dataset = dataContext.multiData?.[dataKey];
    if (!dataset) {
      if (dataContext.multiData && errorCollector) {
        errorCollector.add({
          helperName: metricHelperName,
          message: `Metric "${metricParam}" not found`,
          suggestion: 'Check available metrics in the data panel',
          args: [metricParam],
          severity: 'warning',
        });
      }
      if (defaultValue !== undefined) {
        return defaultValue as T | string;
      }
      if (diagnosticsMode) {
        return makeErrorToken('Unknown field', String(metricParam));
      }
      return emptyReturn(metricParam, options);
    }

    const rawOptions = buildExtractionOptions(options);
    let optionsWithExclude: ExtractionOptions;
    let companyRcid: string | null = null;
    let originalEntityOrLabel = '';
    try {
      const applied = applyCompanyRcidToExtractionOptions(
        rawOptions,
        dataContext.filters
      );
      optionsWithExclude = resolveExcludeEntityInOptions(
        applied.options,
        dataContext.filters
      );
      companyRcid = applied.companyRcid;
      originalEntityOrLabel = applied.originalEntityOrLabel;
    } catch {
      if (diagnosticsMode) {
        return makeErrorToken('Invalid arguments');
      }
      return emptyReturn(metricParam, options);
    }

    let result: T | null = null;
    try {
      result = computationFn(dataset, optionsWithExclude, options);
      if (isEmpty(result) && companyRcid && originalEntityOrLabel) {
        result = computationFn(
          dataset,
          {
            ...optionsWithExclude,
            entity: originalEntityOrLabel,
            label: undefined,
          },
          options
        );
      }
    } catch {
      if (diagnosticsMode) {
        return makeErrorToken('Rendering error');
      }
      return emptyReturn(metricParam, options);
    }
    if (isEmpty(result)) {
      if (errorCollector && metricHelperName) {
        errorCollector.add({
          helperName: metricHelperName,
          message: `Metric "${metricParam}" returned no value for the given options`,
          suggestion: 'Check filters, entity/segment/label, or date range',
          args: [metricParam],
          severity: 'warning',
        });
      }
      if (defaultValue !== undefined && defaultValue !== '') {
        return defaultValue as T | string;
      }
      return emptyReturn(metricParam, options);
    }
    if (wrapHelperOutput) {
      return wrapHelperOutput(
        typeof result === 'string' ? result : String(result)
      ) as unknown as T | string;
    }
    return result as T | string;
  };
};

type HelperDelegate = Handlebars.HelperDelegate;

/**
 * Build the literal helper invocation string (e.g. {{filter "GEOGRAPHY"}}) so we can
 * output it when a helper returns empty string.
 */
function serializeHelperInvocation(
  helperName: string,
  positional: unknown[],
  hash?: Record<string, unknown>
): string {
  const quote = (v: unknown): string =>
    typeof v === 'string'
      ? `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
      : String(v);
  const posStr = positional.map(quote).join(' ');
  const hashStr =
    hash && Object.keys(hash).length > 0
      ? ' ' +
        Object.entries(hash)
          .map(([k, v]) => `${k}=${quote(v)}`)
          .join(' ')
      : '';
  return `{{${helperName}${posStr ? ' ' + posStr : ''}${hashStr}}}`;
}

const registerAggregationHelper = (
  helpersMap: Map<string, HelperDelegate>,
  aggregationType: AggregationType,
  dataContext: DataContext,
  errorCollector?: ErrorCollector,
  hbs?: typeof Handlebars,
  setupOptions?: HandlebarsSetupOptions
) => {
  const computationFn: ComputationFn<number> = (dataset, opts) =>
    computeAggregationFromDataset(dataset, opts, aggregationType);
  const helper = createMetricHelper(
    dataContext,
    computationFn,
    {
      isEmpty: (r) => r === null || Number.isNaN(r ?? NaN),
      helperName: aggregationType,
      diagnosticsMode: setupOptions?.diagnosticsMode ?? false,
      diagnosticsVerbose: setupOptions?.diagnosticsVerbose ?? false,
    },
    errorCollector,
    hbs
  );
  helpersMap.set(aggregationType.toLowerCase(), helper);
};

export type HandlebarsSetupOptions = {
  /** @deprecated No longer used; helper output is no longer wrapped in **. */
  boldHelperOutput?: boolean;
  diagnosticsMode?: boolean;
  diagnosticsVerbose?: boolean;
};

export const handlebarsSetup = (
  dataContext: DataContext,
  errorCollector?: ErrorCollector,
  setupOptions?: HandlebarsSetupOptions
) => {
  const hbs = Handlebars.create();
  const helpersMap = new Map<string, HelperDelegate>();
  const diagnosticsMode = setupOptions?.diagnosticsMode ?? false;
  const diagnosticsVerbose = setupOptions?.diagnosticsVerbose ?? false;

  const renderEmptyToken = (
    helperName: string,
    args: unknown[],
    hash?: Record<string, unknown>
  ): Handlebars.SafeString | string => {
    if (!diagnosticsMode) {
      return new hbs.SafeString(
        serializeHelperInvocation(helperName, args, hash)
      );
    }
    const detail = diagnosticsVerbose
      ? serializeHelperInvocation(helperName, args, hash)
      : undefined;
    return new hbs.SafeString(buildNoDataToken(helperName, detail));
  };

  const renderErrorToken = (
    errorType: string,
    helperName?: string,
    detail?: string
  ): Handlebars.SafeString | string => {
    if (!diagnosticsMode) return '';
    return new hbs.SafeString(buildErrorToken(errorType, helperName, detail));
  };

  const maybeTokenizeLegacyFallback = (
    helperName: string,
    rawResult: unknown,
    args: unknown[]
  ): unknown => {
    if (!diagnosticsMode) return rawResult;
    if (rawResult === null || rawResult === undefined || rawResult === '') {
      const lastArg = args[args.length - 1] as
        | Handlebars.HelperOptions
        | undefined;
      const hash = lastArg?.hash ?? {};
      const positional = args.slice(0, args.length - 1);
      return renderEmptyToken(helperName, positional, hash);
    }
    let asText = '';
    if (typeof rawResult === 'string') {
      asText = rawResult;
    } else if (rawResult instanceof hbs.SafeString) {
      asText = rawResult.toString();
    }
    if (asText.includes('{{')) {
      return renderErrorToken('Invalid expression', helperName);
    }
    return rawResult;
  };
  const wrapHelperOutput: WrapHelperOutputFn = (value) => {
    const raw =
      typeof value === 'string'
        ? value
        : (value as Handlebars.SafeString).toString();
    return new hbs.SafeString(raw);
  };

  const register = (name: string, fn: HelperDelegate) => {
    helpersMap.set(
      name.toLowerCase(),
      function (this: unknown, ...args: unknown[]) {
        try {
          const raw = (fn as (...a: unknown[]) => unknown).apply(this, args);
          return maybeTokenizeLegacyFallback(name, raw, args);
        } catch {
          return renderErrorToken('Rendering error', name);
        }
      }
    );
  };

  // Register aggregation helpers from shared schema: SUM, AVG, MIN, MAX, COUNT (no bold - inner () results)
  AGGREGATION_TYPES.forEach((type) => {
    registerAggregationHelper(
      helpersMap,
      type,
      dataContext,
      errorCollector,
      hbs,
      setupOptions
    );
  });

  // MIN/MAX: aggregation (single metric string) and multi-argument numeric
  const minMetricHelper = createMetricHelper(
    dataContext,
    (dataset, opts) => computeAggregationFromDataset(dataset, opts, 'MIN'),
    {
      isEmpty: (r) => r === null || Number.isNaN(r ?? NaN),
      helperName: 'MIN',
      diagnosticsMode,
      diagnosticsVerbose,
    },
    errorCollector,
    hbs
  );

  register('MIN', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const values = args.slice(0, args.length - 1);
    if (values.length >= 2) {
      const numbers = values
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      if (numbers.length > 0) {
        return String(Math.min(...numbers));
      }
    }
    if (values.length === 1 && isString(values[0])) {
      return minMetricHelper(values[0], options);
    }
    if (errorCollector) {
      errorCollector.add({
        helperName: 'MIN',
        message: 'MIN requires one metric name or two or more numbers',
        suggestion: 'Use {{MIN "metric"}} or {{MIN a b}}',
        args: values,
        severity: 'warning',
      });
    }
    return new hbs.SafeString(
      serializeHelperInvocation('MIN', values, options.hash)
    );
  });

  const maxMetricHelper = createMetricHelper(
    dataContext,
    (dataset, opts) => computeAggregationFromDataset(dataset, opts, 'MAX'),
    {
      isEmpty: (r) => r === null || Number.isNaN(r ?? NaN),
      helperName: 'MAX',
      diagnosticsMode,
      diagnosticsVerbose,
    },
    errorCollector,
    hbs
  );

  register('MAX', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const values = args.slice(0, args.length - 1);
    if (values.length >= 2) {
      const numbers = values
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      if (numbers.length > 0) {
        return String(Math.max(...numbers));
      }
    }
    if (values.length === 1 && isString(values[0])) {
      return maxMetricHelper(values[0], options);
    }
    if (errorCollector) {
      errorCollector.add({
        helperName: 'MAX',
        message: 'MAX requires one metric name or two or more numbers',
        suggestion: 'Use {{MAX "metric"}} or {{MAX a b}}',
        args: values,
        severity: 'warning',
      });
    }
    return new hbs.SafeString(
      serializeHelperInvocation('MAX', values, options.hash)
    );
  });

  /**
   * VALUE helper: extracts a single value from any dataset type using the same options
   * as aggregations plus dimensional selectors.
   *
   * Usage:
   * - {{VALUE "headcount" entity="Workday"}}
   * - {{VALUE "headcount" entity=(filter "COMPANY")}}
   * - {{VALUE "headcount" secondaryDimension="geography" segment="Western Europe" entity="Workday"}}
   *
   * Common options:
   * - entity="Workday"            (SINGLE/TIMESERIES/BREAKDOWN_* item dim category)
   * - secondaryDimension="geography" (BREAKDOWN_* outer dim)
   * - segment="Western Europe"    (BREAKDOWN_* outer dim category)
   * - label="Compensation"        (SENTIMENT_SINGLE / MAP point label)
   * - percentile="q90"            (BOX/BREAKDOWN_BOX)
   * - timeAggregation="latest"    (TIMESERIES/BREAKDOWN_TIMESERIES)
   * - date="2024-12-01"           (TIMESERIES/BREAKDOWN_TIMESERIES)
   * - field="salary_base"         (TABLE_ROWS) / field="count" (SENTIMENT_SINGLE)
   * - whereField="current_company" equals="Workday" (TABLE_ROWS row filter)
   * - reduce="first|last|min|max|sum|avg"          (collapse if multiple values)
   */
  const valueComputationFn: ComputationFn<string | number> = (
    dataset,
    opts,
    helperOptions
  ) => {
    const hash = getHelperHash(helperOptions);
    const defaultReduce: AggregationFunction = 'last';
    if (dataset.type === DataType.TEXT) {
      const targetEntity = opts.entity || opts.label;
      const point = dataset.data.find((d) =>
        targetEntity ? d.entity === targetEntity : true
      );
      if (point) return point.text || '';
      if (dataset.data.length === 1) return dataset.data[0]?.text || '';
      return '';
    }
    if (dataset.type === DataType.BREAKDOWN_SINGLE) {
      const hasSegment =
        opts.segment !== undefined && String(opts.segment).trim() !== '';
      const hasEntity =
        opts.entity !== undefined && String(opts.entity).trim() !== '';
      const hasLabel =
        opts.label !== undefined && String(opts.label).trim() !== '';
      if (!hasSegment && !hasEntity && !hasLabel) {
        return null;
      }
    }
    const values = extractDatasetValues(dataset, opts);
    const reduce = getReduceOption(hash, defaultReduce);
    const reduced = reduceNumericValues(values, reduce);
    return reduced !== null && Number.isFinite(reduced) ? reduced : null;
  };
  register(
    'VALUE',
    createMetricHelper(
      dataContext,
      valueComputationFn,
      { helperName: 'VALUE', diagnosticsMode, diagnosticsVerbose },
      errorCollector,
      hbs
    )
  );

  /**
   * HAS_DATA helper: true if the metric dataset contains at least one extracted value
   * after applying selection options. Useful for conditionals.
   */
  const hasDataComputationFn: ComputationFn<boolean> = (dataset, opts) => {
    const values = extractDatasetValues(dataset, opts);
    return values.length > 0;
  };
  register(
    'HAS_DATA',
    createMetricHelper(
      dataContext,
      hasDataComputationFn,
      {
        defaultValue: false,
        isEmpty: (r) => r === false,
        helperName: 'HAS_DATA',
        diagnosticsMode,
        diagnosticsVerbose,
      },
      errorCollector,
      hbs
    )
  );

  /**
   * ARG_MAX: returns the entity label with the maximum value for a metric.
   */
  register(
    'ARG_MAX',
    createMetricHelper(
      dataContext,
      (dataset, opts, helperOptions) => {
        const reduce = getReduceOption(getHelperHash(helperOptions), 'last');
        return getArgMax(opts, dataset, reduce) || null;
      },
      { helperName: 'ARG_MAX', diagnosticsMode, diagnosticsVerbose },
      errorCollector,
      hbs,
      wrapHelperOutput
    )
  );

  /**
   * ARG_MIN: returns the entity label with the minimum value for a metric.
   */
  register(
    'ARG_MIN',
    createMetricHelper(
      dataContext,
      (dataset, opts, helperOptions) => {
        const reduce = getReduceOption(getHelperHash(helperOptions), 'last');
        return getArgMin(opts, dataset, reduce) || null;
      },
      { helperName: 'ARG_MIN', diagnosticsMode, diagnosticsVerbose },
      errorCollector,
      hbs,
      wrapHelperOutput
    )
  );

  /**
   * LIST: returns entity labels as a natural language list ("A, B, and C").
   * Usage: {{LIST "target_companies"}}, {{LIST "headcount" limit="3"}}
   */
  register(
    'LIST',
    (metricParam: unknown, options: Handlebars.HelperOptions) => {
      if (!isString(metricParam)) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'LIST',
            message: 'First argument must be a metric name (string)',
            suggestion: 'Use {{LIST "headcount"}}',
            args: [metricParam],
          });
        }
        return '';
      }
      const hash = getHelperHash(options);
      const dataKey = metricParam as MetricValues;
      const dataset = dataContext.multiData?.[dataKey];
      if (!dataset) {
        if (dataContext.multiData && errorCollector) {
          errorCollector.add({
            helperName: 'LIST',
            message: `Metric "${metricParam}" not found`,
            suggestion: 'Check available metrics in the data panel',
            args: [metricParam],
          });
        }
        return '';
      }

      const rawOptions = buildExtractionOptions(options);
      const { options: extractionOptions } =
        applyCompanyRcidToExtractionOptions(rawOptions, dataContext.filters);
      const optionsWithExclude = resolveExcludeEntityInOptions(
        extractionOptions,
        dataContext.filters
      );
      const reduce = getReduceOption(hash, 'last');
      const sortDirection =
        hash.sort === 'asc' ? ('asc' as const) : ('desc' as const);
      const limitRaw = hash.limit;
      let limit: number | undefined;
      if (typeof limitRaw === 'number' && limitRaw > 0) {
        limit = Math.floor(limitRaw);
      } else if (
        typeof limitRaw === 'string' &&
        /^\d+$/.test(limitRaw) &&
        parseInt(limitRaw, 10) > 0
      ) {
        limit = parseInt(limitRaw, 10);
      }

      const rows = getSortedEntityValues(
        { ...optionsWithExclude, entity: undefined, label: undefined },
        dataset,
        reduce,
        sortDirection
      );
      const limited = limit !== undefined ? rows.slice(0, limit) : rows;
      if (limited.length === 0) return '';

      const names = limited.map((r) => r.entity);
      if (names.length === 1) return wrapHelperOutput(names[0] ?? '');
      if (names.length === 2) {
        return wrapHelperOutput(`${names[0]} and ${names[1]}`);
      }

      const allButLast = names.slice(0, -1).join(', ');
      return wrapHelperOutput(`${allButLast}, and ${names[names.length - 1]}`);
    }
  );

  /**
   * TABLE: renders a markdown table of entity labels and values for a metric.
   * Usage: {{TABLE "headcount"}}, {{TABLE "headcount" format="SI"}}, {{TABLE "headcount" reduce="avg" limit="10"}}
   */
  register(
    'TABLE',
    (metricParam: unknown, options: Handlebars.HelperOptions) => {
      if (!isString(metricParam)) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'TABLE',
            message: 'First argument must be a metric name (string)',
            suggestion: 'Use {{TABLE "headcount"}}',
            args: [metricParam],
          });
        }
        return '';
      }
      const hash = getHelperHash(options);
      const tableDataKey = metricParam as MetricValues;
      const dataset = dataContext.multiData?.[tableDataKey];
      if (!dataset) {
        if (dataContext.multiData && errorCollector) {
          errorCollector.add({
            helperName: 'TABLE',
            message: `Metric "${metricParam}" not found`,
            suggestion: 'Check available metrics in the data panel',
            args: [metricParam],
            severity: 'warning',
          });
        }
        return '';
      }
      const rawOptions = buildExtractionOptions(options);
      const { options: extractionOptions } =
        applyCompanyRcidToExtractionOptions(rawOptions, dataContext.filters);
      const optionsWithExclude = resolveExcludeEntityInOptions(
        extractionOptions,
        dataContext.filters
      );
      const reduce = getReduceOption(hash, 'sum');
      const limitRaw = hash.limit;
      const limit =
        // eslint-disable-next-line no-nested-ternary
        typeof limitRaw === 'number' && limitRaw > 0
          ? Math.floor(limitRaw)
          : // biome-ignore lint/style/noNestedTernary: migrated from develop
            typeof limitRaw === 'string' && /^\d+$/.test(limitRaw)
            ? parseInt(limitRaw, 10)
            : undefined;
      const formatArg = hash.format as string | undefined;
      const formatType = formatArg ? resolveFormatType(formatArg) : null;
      const formatValue = getFormatter(formatType ?? FormatType.SI);

      const rows = getSortedEntityValues(
        { ...optionsWithExclude, entity: undefined, label: undefined },
        dataset,
        reduce,
        'desc'
      );
      const limited = limit !== undefined ? rows.slice(0, limit) : rows;
      if (limited.length === 0) {
        return new hbs.SafeString('');
      }

      const escapeCell = (s: string) =>
        s.replace(/\|/g, '\\|').replace(/\n/g, ' ');
      const header = '| Label | Value |';
      const separator = '| --- | --- |';
      const body = limited
        .map(
          (r) =>
            `| ${escapeCell(r.entity)} | ${escapeCell(formatValue(r.value))} |`
        )
        .join('\n');
      return new hbs.SafeString([header, separator, body].join('\n'));
    }
  );

  /**
   * MAX_POINT: returns the date, value, or entity for the maximum point in a time series.
   * Requires TIMESERIES or BREAKDOWN_TIMESERIES dataset type.
   * Supports entity as second positional: {{MAX_POINT "overall" (filter "COMPANY") property="date"}}
   */
  const maxPointComputationFn: ComputationFn<string | number> = (
    dataset,
    opts,
    helperOptions
  ) => {
    const property = getPointProperty(getHelperHash(helperOptions));
    if (
      dataset.type !== 'TIMESERIES' &&
      dataset.type !== 'BREAKDOWN_TIMESERIES'
    ) {
      return null;
    }
    if (property === 'entity') {
      const r = getMaxPointEntity(opts, dataset);
      return r || null;
    }
    const point = getMaxPoint(opts, dataset);
    if (!point) return null;
    if (property === 'x' || property === 'date') return point.date ?? null;
    return Number.isFinite(point.value) ? point.value : null;
  };
  register('MAX_POINT', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const metricParam = args[0];
    if (!isString(metricParam)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'MAX_POINT',
          message: 'First argument must be a metric name (string)',
          suggestion: 'Use a timeseries metric, e.g. {{MAX_POINT "overall"}}',
          args: [metricParam],
        });
      }
      return new hbs.SafeString(
        serializeHelperInvocation('MAX_POINT', args.slice(0, -1), options.hash)
      );
    }
    const mergedOptions = mergeEntityIntoOptions(args, options);
    const helper = createMetricHelper(
      dataContext,
      maxPointComputationFn,
      {
        isEmpty: (r) => r === null || r === '',
        helperName: 'MAX_POINT',
        diagnosticsMode,
        diagnosticsVerbose,
      },
      errorCollector,
      hbs
    );
    return helper(metricParam, mergedOptions);
  });

  /**
   * MIN_POINT: returns the date, value, or entity for the minimum point in a time series.
   * Supports entity as second positional: {{MIN_POINT "overall" (filter "COMPANY") property="date"}}
   */
  const minPointComputationFn: ComputationFn<string | number> = (
    dataset,
    opts,
    helperOptions
  ) => {
    const property = getPointProperty(getHelperHash(helperOptions));
    if (
      dataset.type !== 'TIMESERIES' &&
      dataset.type !== 'BREAKDOWN_TIMESERIES'
    ) {
      return null;
    }
    if (property === 'entity') {
      const r = getMinPointEntity(opts, dataset);
      return r || null;
    }
    const point = getMinPoint(opts, dataset);
    if (!point) return null;
    if (property === 'x' || property === 'date') return point.date ?? null;
    return Number.isFinite(point.value) ? point.value : null;
  };
  register('MIN_POINT', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const metricParam = args[0];
    if (!isString(metricParam)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'MIN_POINT',
          message: 'First argument must be a metric name (string)',
          suggestion: 'Use a timeseries metric, e.g. {{MIN_POINT "overall"}}',
          args: [metricParam],
        });
      }
      return new hbs.SafeString(
        serializeHelperInvocation('MIN_POINT', args.slice(0, -1), options.hash)
      );
    }
    const mergedOptions = mergeEntityIntoOptions(args, options);
    const helper = createMetricHelper(
      dataContext,
      minPointComputationFn,
      {
        isEmpty: (r) => r === null || r === '',
        helperName: 'MIN_POINT',
        diagnosticsMode,
        diagnosticsVerbose,
      },
      errorCollector,
      hbs
    );
    return helper(metricParam, mergedOptions);
  });

  /**
   * SHARE: returns the share (ratio) that a specific entity's value represents
   * of the total sum for a metric. Returns a decimal between 0 and 1.
   * Usage:
   * - {{SHARE "headcount" entity="Workday"}} -> share of Workday's headcount
   * - {{SHARE "headcount" entity=(ARG_MAX "headcount")}} -> share of the max entity
   */
  const shareComputationFn: ComputationFn<number> = (
    dataset,
    opts,
    helperOptions
  ) => {
    const { entity: _e, label: _l, segment: optsSegment, ...restOpts } = opts;
    const optsEntity = opts.entity?.trim();
    const segmentTrimmed = optsSegment?.trim();

    const isBreakdownWithBoth =
      (dataset.type === DataType.BREAKDOWN_SINGLE ||
        (dataset.type === DataType.BOX && isBoxBreakdownDataSet(dataset))) &&
      segmentTrimmed !== undefined &&
      segmentTrimmed !== '' &&
      optsEntity !== undefined &&
      optsEntity !== '';

    let totalOptions: ExtractionOptions;
    if (isBreakdownWithBoth) {
      const baseTotal = { ...restOpts };
      const withSegmentA = { ...baseTotal, segment: segmentTrimmed };
      const withSegmentB = { ...baseTotal, segment: optsEntity };
      const valuesA = extractDatasetValues(dataset, withSegmentA);
      totalOptions = valuesA.length > 0 ? withSegmentA : withSegmentB;
    } else {
      totalOptions = { ...restOpts };
    }

    const totalValues = extractDatasetValues(dataset, totalOptions);
    const numericTotalValues = totalValues.filter(
      (v): v is number => v !== null && Number.isFinite(v)
    );
    if (numericTotalValues.length === 0) return null;
    const sumValue = numericTotalValues.reduce((acc, v) => acc + v, 0);
    if (sumValue === 0) return null;

    const entityValues = extractDatasetValues(dataset, opts);
    const numericEntityValues = entityValues.filter(
      (v): v is number => v !== null && Number.isFinite(v)
    );
    if (numericEntityValues.length === 0) return null;

    const reduce = getReduceOption(getHelperHash(helperOptions), 'sum');
    const entityValue = reduceNumericValues(numericEntityValues, reduce);
    if (entityValue === null || !Number.isFinite(entityValue)) return null;

    const share = entityValue / sumValue;
    return Number.isFinite(share) ? share : null;
  };
  register(
    'SHARE',
    createMetricHelper(
      dataContext,
      shareComputationFn,
      {
        isEmpty: (r) =>
          r === null || (typeof r === 'number' && Number.isNaN(r)),
        helperName: 'SHARE',
        diagnosticsMode,
        diagnosticsVerbose,
      },
      errorCollector,
      hbs
    )
  );

  /**
   * RANK: returns the 1-based rank of an entity for a metric.
   * Usage:
   * - {{RANK "headcount" "Workday"}}
   * - {{RANK "headcount" entity="Workday"}}
   * - {{RANK "headcount" (filter "COMPANY")}}
   *
   * Ranking is by value DESC; ties are broken by entity label ASC.
   * Supports company RCID resolution from filters for improved filter integration.
   */
  const rankComputationFn: ComputationFn<number | null> = (
    dataset,
    opts,
    helperOptions
  ) => {
    const entityKey = opts.entity?.trim() || opts.label?.trim();
    if (!entityKey) return null;
    const reduce = getReduceOption(getHelperHash(helperOptions), 'avg');
    return getRank(entityKey, opts, dataset, reduce);
  };
  register('RANK', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const metricParam = args[0];
    if (!isString(metricParam)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'RANK',
          message: 'First argument must be a metric name (string)',
          suggestion: 'Use {{RANK "headcount" entity="Workday"}}',
          args: [metricParam],
        });
      }
      return '';
    }
    const mergedOptions = mergeEntityIntoOptions(args, options);
    const entityParam = mergedOptions.hash?.entity;
    if (entityParam == null || String(entityParam).trim() === '') {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'RANK',
          message: 'RANK requires an entity (second argument or entity= hash)',
          suggestion:
            'Use {{RANK "headcount" (filter "COMPANY")}} or entity="Workday"',
          args: [metricParam],
        });
      }
      return '';
    }
    const helper = createMetricHelper(
      dataContext,
      rankComputationFn,
      {
        isEmpty: (r) =>
          r === null || (typeof r === 'number' && Number.isNaN(r)),
        helperName: 'RANK',
        diagnosticsMode,
        diagnosticsVerbose,
      },
      errorCollector,
      hbs
    );
    return helper(metricParam, mergedOptions);
  });

  /**
   * PERCENTILE_RANK: Multi-metric percentile rank.
   * Per metric per entity: reduce="avg"|"median" (default) uses simple reduce; reduce="time_weighted_avg" uses step-function weights over last 24 months.
   * Composite = mean of per-metric scores per entity. Returns percentile (0–1) where rank 1 = lowest.
   *
   * Usage:
   * - {{PERCENTILE_RANK "overall" entity=(filter "COMPANY")}} (default: median)
   * - {{PERCENTILE_RANK "overall" entity=(filter "COMPANY") reduce="avg"}}
   * - {{PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(filter "COMPANY") reduce="time_weighted_avg"}}
   */
  register('PERCENTILE_RANK', (...args: unknown[]) => {
    const options = args[args.length - 1] as Handlebars.HelperOptions;
    const rest = args.slice(0, args.length - 1);
    const metricKeys: string[] = rest.filter(
      (x): x is string =>
        typeof x === 'string' &&
        Boolean(dataContext.multiData?.[x as MetricValues])
    ) as string[];
    if (metricKeys.length === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PERCENTILE_RANK',
          message: 'PERCENTILE_RANK requires at least one metric name',
          suggestion:
            'Use {{PERCENTILE_RANK "overall" entity=(filter "COMPANY")}}',
          args: rest,
        });
      }
      return '';
    }

    const extractionOptions = buildExtractionOptions(options);
    const rawEntityParam =
      options.hash?.entity ??
      (rest.length > metricKeys.length ? rest[metricKeys.length] : undefined);
    const entityParam = normalizeHashValue(rawEntityParam);
    let targetEntity = '';
    if (typeof entityParam === 'string') {
      targetEntity = entityParam.trim();
    } else {
      targetEntity = (
        extractionOptions.entity ??
        extractionOptions.label ??
        ''
      ).trim();
    }
    const companyRcid = resolveCompanyRcidFromFilters(
      targetEntity,
      dataContext.filters
    );
    const finalEntity = companyRcid ?? targetEntity;
    if (!finalEntity) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PERCENTILE_RANK',
          message:
            'PERCENTILE_RANK requires an entity (entity= hash or second argument)',
          suggestion: 'Use entity=(filter "COMPANY") or a literal entity name',
          args: [metricKeys, entityParam],
        });
      }
      return '';
    }

    const validDatasets: DataProviderSet[] = [];
    metricKeys.forEach((key) => {
      const ds = dataContext.multiData?.[key as MetricValues];
      if (ds) validDatasets.push(ds);
    });
    if (validDatasets.length === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PERCENTILE_RANK',
          message: 'No dataset available for the given metrics',
          suggestion: 'Ensure metrics are loaded; check data panel',
          args: [metricKeys],
          severity: 'warning',
        });
      }
      return '';
    }

    const optionsWithEntity = {
      ...extractionOptions,
      entity: finalEntity,
      label: undefined,
    };
    const optionsWithExclude = resolveExcludeEntityInOptions(
      optionsWithEntity,
      dataContext.filters
    );
    const hash = getHelperHash(options);
    const reduce: AggregationFunction = isValidAggregationFunction(hash.reduce)
      ? (hash.reduce as AggregationFunction)
      : 'avg';
    const result = getCompositePercentileRank(
      finalEntity,
      validDatasets,
      optionsWithExclude,
      reduce
    );
    if (result === null || !Number.isFinite(result)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PERCENTILE_RANK',
          message:
            'PERCENTILE_RANK returned no value for the given entity and metrics',
          suggestion: 'Check that the entity has data for the selected metrics',
          args: [finalEntity, metricKeys],
          severity: 'warning',
        });
      }
      return '';
    }
    return String(result);
  });

  // Register FORMAT helper that wraps aggregation results with formatting (bold {{ }} output only)
  // Usage: {{FORMAT (SUM "headcount") format="SI"}}, {{FORMAT (RANK ...) format="ORDINAL"}}, {{FORMAT (MAX_POINT ... property="date") format="DATE"}}
  register(
    'FORMAT',
    (value: number | string, options: Handlebars.HelperOptions) => {
      if (value === null || value === undefined) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'FORMAT',
            message: 'FORMAT requires a value (first argument)',
            suggestion:
              'Wrap an aggregation in parentheses, e.g. {{FORMAT (SUM "headcount")}}',
            args: [value],
            severity: 'warning',
          });
        }
        return '';
      }

      const formatArg = getHelperHash(options).format as string | undefined;
      const specialHandler =
        formatArg && formatArg in TEMPLATE_FORMAT_HANDLERS
          ? TEMPLATE_FORMAT_HANDLERS[
              formatArg as keyof typeof TEMPLATE_FORMAT_HANDLERS
            ]
          : null;

      if (specialHandler) {
        return wrapHelperOutput(specialHandler(value));
      }

      let num: number;
      if (typeof value === 'number' && Number.isFinite(value)) {
        num = value;
      } else {
        const raw = typeof value === 'string' ? value : String(value);
        const stripped = raw.replace(/^\s*\*\*|\*\*\s*$/g, '').trim();
        num = Number(stripped);
      }
      if (!Number.isFinite(num)) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'FORMAT',
            message: 'Value is not a finite number',
            suggestion: 'Ensure the inner helper returns a number',
            args: [value],
            severity: 'warning',
          });
        }
        return '';
      }

      const decimalPrecisionMatch = formatArg?.match(DECIMAL_PRECISION_REGEX);
      const percentileDecimalMatch = formatArg?.match(PERCENTILE_DECIMAL_REGEX);
      const formatType = (() => {
        if (!formatArg) return FormatType.SI;
        if (decimalPrecisionMatch || percentileDecimalMatch) return formatArg;
        return resolveFormatType(formatArg);
      })();

      const formatValue = getFormatter(formatType ?? FormatType.SI);
      return wrapHelperOutput(formatValue(num));
    }
  );

  // Register arithmetic helpers for basic mathematical operations
  // Usage: {{add (SUM "x") (SUM "y")}}, {{divide (SUM "revenue") (COUNT "employees")}}
  register('add', (...args: unknown[]) => {
    // Last argument is always the Handlebars options object
    const raw = args.slice(0, -1);
    const numbers = raw.map(Number).filter(Number.isFinite);
    if (numbers.length === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'add',
          message: 'Invalid numeric arguments',
          suggestion: 'Ensure all arguments are valid numbers',
          args: raw,
        });
      }
      return '';
    }
    return String(numbers.reduce((sum, n) => sum + n, 0));
  });

  register('subtract', (a: unknown, b: unknown) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!Number.isFinite(numA) || !Number.isFinite(numB)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'subtract',
          message: 'Invalid numeric arguments',
          suggestion: 'Ensure both arguments are valid numbers',
          args: [a, b],
        });
      }
      return '';
    }
    return String(numA - numB);
  });

  register('multiply', (...args: unknown[]) => {
    const raw = args.slice(0, -1);
    const numbers = raw.map(Number).filter(Number.isFinite);
    if (numbers.length === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'multiply',
          message: 'Invalid numeric arguments',
          suggestion: 'Ensure all arguments are valid numbers',
          args: raw,
        });
      }
      return '';
    }
    return String(numbers.reduce((product, n) => product * n, 1));
  });

  register('divide', (a: unknown, b: unknown) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!Number.isFinite(numA) || !Number.isFinite(numB)) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'divide',
          message: 'Invalid numeric arguments',
          suggestion: 'Ensure both arguments are valid numbers',
          args: [a, b],
        });
      }
      return '';
    }
    if (numB === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'divide',
          message: 'Division by zero',
          suggestion: 'Check that the divisor is not zero',
          args: [a, b],
        });
      }
      return '';
    }
    return String(numA / numB);
  });

  register('MEAN', (...args: unknown[]) => {
    const raw = args.slice(0, -1);
    const numbers = raw.map(Number).filter(Number.isFinite);
    if (numbers.length === 0) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'MEAN',
          message: 'MEAN requires at least one valid number',
          suggestion: 'Pass numeric values or aggregation results',
          args: raw,
          severity: 'warning',
        });
      }
      return '';
    }
    return String(numbers.reduce((sum, n) => sum + n, 0) / numbers.length);
  });

  register('MATH', (expression: unknown, options: Handlebars.HelperOptions) => {
    if (typeof expression !== 'string') {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'MATH',
          message: 'Expression must be a string',
          suggestion: 'Wrap the expression in quotes, e.g. {{MATH "x / y"}}',
          args: [expression],
        });
      }
      return '';
    }

    const rawHash = options.hash || {};
    const variables: Record<string, number> = {};
    for (const [key, value] of Object.entries(rawHash)) {
      const num = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(num)) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'MATH',
            message: `Variable "${key}" must be a number`,
            suggestion:
              'Ensure the variable value resolves to a number (e.g. SUM/AVG/COUNT results)',
            args: [key, value],
          });
        }
        return '';
      }
      variables[key] = num;
    }

    try {
      const result = evaluateMathExpression(expression, variables);

      // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
      if (typeof result !== 'number' || !isFinite(result)) {
        if (errorCollector) {
          errorCollector.add({
            helperName: 'MATH',
            message: 'Result is not a valid number',
            suggestion:
              'Check for invalid operations (e.g. division by zero) or missing variables',
            args: [expression, variables],
          });
        }
        return '';
      }

      return String(result);
    } catch (error) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'MATH',
          message:
            error instanceof Error
              ? error.message
              : `Unknown error: ${String(error)}`,
          suggestion:
            'Check expression syntax and that all referenced variables exist',
          args: [expression, variables],
        });
      }
      return '';
    }
  });

  register('filter', (filterName) => {
    if (typeof filterName !== 'string') {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'filter',
          message: 'filter requires a filter ID (string)',
          suggestion: 'Use {{filter "COMPANY"}} or another filter ID',
          args: [filterName],
        });
      }
      if (diagnosticsMode) {
        return renderErrorToken('Invalid arguments', 'filter');
      }
      return wrapHelperOutput(
        new hbs.SafeString(serializeHelperInvocation('filter', [filterName]))
      );
    }
    const filter = dataContext.filters.find((f) => f.id === filterName);
    if (!filter) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'filter',
          message: `Filter "${filterName}" not found`,
          suggestion:
            'Check filter ID; common IDs: COMPANY, GEOGRAPHY, DATE_RANGE',
          args: [filterName],
          severity: 'warning',
        });
      }
      if (diagnosticsMode) {
        return renderErrorToken('Missing filter', 'filter', filterName);
      }
      return wrapHelperOutput(
        new hbs.SafeString(serializeHelperInvocation('filter', [filterName]))
      );
    }

    return wrapHelperOutput(formatFilterValue(filter));
  });

  register('PARAM', (paramName) => {
    const emptyReturn = () =>
      wrapHelperOutput(
        new hbs.SafeString(serializeHelperInvocation('PARAM', [paramName]))
      );

    if (typeof paramName !== 'string') {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PARAM',
          message: 'PARAM requires a parameter name (first argument, string)',
          suggestion: 'Use {{PARAM "Company"}} or {{PARAM "Competitors"}}',
          args: [paramName],
        });
      }
      return emptyReturn();
    }

    const sourceFilters = dataContext.parameterFilters ?? dataContext.filters;
    const match = sourceFilters.find((f) => f.name === paramName);
    if (!match) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'PARAM',
          message: `No parameter found with name "${paramName}"`,
          suggestion:
            'Check the parameter name matches a reportParameters[].name value',
          args: [paramName],
          severity: 'warning',
        });
      }
      return emptyReturn();
    }

    return wrapHelperOutput(formatFilterValue(match));
  });

  register('HAS_PARAM', (paramName) => {
    if (typeof paramName !== 'string') {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'HAS_PARAM',
          message:
            'HAS_PARAM requires a parameter name (first argument, string)',
          suggestion:
            'Use {{HAS_PARAM "Occupation"}} or {{HAS_PARAM "Company"}}',
          args: [paramName],
        });
      }
      return false;
    }

    const sourceFilters = dataContext.parameterFilters ?? dataContext.filters;
    const match = sourceFilters.find((f) => f.name === paramName);
    if (!match) {
      return false;
    }

    return formatFilterValue(match).trim() !== '';
  });

  // Register metric helper to access the currently selected metric
  // Returns the display title from metricToTitleMap instead of the raw value
  // Returns SafeString to prevent Handlebars from escaping HTML entities like &
  register('metric', () => {
    const metric = dataContext.currentMetric;
    if (!metric) {
      if (errorCollector) {
        errorCollector.add({
          helperName: 'metric',
          message: 'No metric is currently selected',
          suggestion:
            'Select a metric in the dashboard or use a specific metric key in another helper',
          args: [],
          severity: 'warning',
        });
      }
      return wrapHelperOutput(
        new hbs.SafeString(serializeHelperInvocation('metric', []))
      );
    }
    const title = metricToTitleMap[metric] ?? metric;
    return wrapHelperOutput(new hbs.SafeString(title));
  });

  // Register comparison helpers from shared schema
  // These are validated against COMPARISON_OPERATORS to ensure consistency
  const comparisonImplementations: Record<
    (typeof COMPARISON_OPERATORS)[number],
    (a: unknown, b: unknown) => boolean
  > = {
    gt: (a, b) => parseFloat(String(a)) > parseFloat(String(b)),
    gte: (a, b) => parseFloat(String(a)) >= parseFloat(String(b)),
    lt: (a, b) => parseFloat(String(a)) < parseFloat(String(b)),
    lte: (a, b) => parseFloat(String(a)) <= parseFloat(String(b)),
    eq: (a, b) => a === b,
  };

  COMPARISON_OPERATORS.forEach((op) => {
    register(op, (a: unknown, b: unknown) =>
      comparisonImplementations[op](a, b)
    );
  });

  hbs.registerHelper('__empty', (message?: unknown) => {
    if (message != null && typeof message === 'string') {
      return new Handlebars.SafeString(message);
    }
    return renderEmptyToken('__empty', [], {});
  });

  hbs.registerHelper('__hb_error', (message?: unknown) => {
    if (typeof message === 'string' && message.trim().length > 0) {
      return renderErrorToken(message);
    }
    return renderErrorToken('Rendering error');
  });

  // Case-insensitive dispatch: any {{HelperName}} triggers helperMissing.
  // Never throw so one invalid helper (e.g. {{}} or {{INVALID}}) does not blank the whole preview.
  hbs.registerHelper(
    'helperMissing',
    function (this: unknown, ...args: unknown[]) {
      try {
        const options = args[args.length - 1] as Handlebars.HelperOptions & {
          name?: string;
        };
        const rawName = options.name;
        const helperName =
          typeof rawName === 'string' && rawName.trim().length > 0
            ? rawName
            : '';
        if (!helperName) {
          if (errorCollector) {
            errorCollector.add({
              helperName: 'COMPILATION',
              message: 'Empty helper {{}} is invalid',
              suggestion: 'Use a helper name, e.g. {{SUM "headcount"}}',
              args: [],
            });
          }
          return diagnosticsMode ? renderErrorToken('Empty expression') : '';
        }
        const impl = helpersMap.get(helperName.toLowerCase());
        if (impl) {
          return (impl as (...a: unknown[]) => unknown).apply(this, args);
        }
        const helperArgs = args.slice(0, args.length - 1);
        if (errorCollector) {
          const availableHelpers = Object.values(HELPER_REGISTRY)
            .map((helper) => helper.name)
            .join(', ');
          errorCollector.add({
            helperName,
            message: `Helper "${helperName}" does not exist`,
            suggestion: `Use one of: ${availableHelpers}`,
            args: helperArgs,
          });
        }
        if (!diagnosticsMode) {
          return new Handlebars.SafeString(helperName + '(' + helperArgs + ')');
        }
        return renderErrorToken('Unknown helper', helperName);
      } catch {
        return diagnosticsMode ? renderErrorToken('Rendering error') : '';
      }
    }
  );

  // TODO: this helper is a workaround. The paragraph needs, per skill in the
  // breakdown, `target_share - comparison_share`, then the skill with the
  // biggest/smallest diff plus the diff value. Our text-chart helpers (VALUE,
  // SHARE, ARG_MAX, ...) all return a single scalar — the breakdown array
  // stays inside the helper and is never exposed to the template, so there's
  // nothing for `{{#each}}` to loop over and do per-skill arithmetic on.
  // Would need either a block helper that yields each breakdown item to the
  // template (e.g. `{{#EACH_BREAKDOWN "sq:..."}}...{{/EACH_BREAKDOWN}}`) or a
  // purpose-built ARG_MAX_DIFF / MAX_DIFF that takes two dataset slices.
  // Filter names are hardwired to the market-conditions report; once the
  // missing building blocks exist, inline this in the template and drop it.
  register('SKILLS_EXPLANATION', (metricKey: unknown) => {
    if (typeof metricKey !== 'string') return '';
    const dataset = dataContext.multiData?.[metricKey as MetricValues];
    if (!dataset || dataset.type !== 'BREAKDOWN_SINGLE') {
      return diagnosticsMode
        ? new hbs.SafeString(buildNoDataToken('SKILLS_EXPLANATION'))
        : '';
    }

    const targetGeoParam = dataContext.parameterFilters?.find(
      (f) => f.name === 'Target Geography'
    );
    const targetGeo = targetGeoParam ? formatFilterValue(targetGeoParam) : '';
    const comparisonGeoParam = dataContext.parameterFilters?.find(
      (f) => f.name === 'Comparison Geography'
    );
    const comparisonGeoNames = (() => {
      if (!comparisonGeoParam) return [];
      const formatted = formatFilterValue(comparisonGeoParam);
      if (!formatted || formatted === 'Comparison Geography') return [];
      return formatted.split(', ').filter(Boolean);
    })();
    const comparisonGeoLabel = (() => {
      if (comparisonGeoNames.length === 1) {
        const name = comparisonGeoNames[0];
        return name === 'United States' || name === 'United Kingdom'
          ? `the ${name}`
          : name;
      }
      return 'the comparison geography';
    })();
    const occupationParam = dataContext.parameterFilters?.find(
      (f) => f.name === 'Occupation'
    );
    const occupation = occupationParam
      ? formatFilterValue(occupationParam)
      : '';
    const subjectClause = occupation ? `the ${occupation} role` : 'these roles';

    const targetGroup = dataset.data.find(
      (g) => g.dim.toLowerCase() === targetGeo.toLowerCase()
    );
    const primaryComparison = comparisonGeoNames[0]?.toLowerCase();
    const anyNonTarget = dataset.data.find(
      (g) => g.dim.toLowerCase() !== targetGeo.toLowerCase()
    );
    const comparisonGroup = primaryComparison
      ? (dataset.data.find((g) => g.dim.toLowerCase() === primaryComparison) ??
        anyNonTarget)
      : anyNonTarget;

    if (
      !targetGroup?.breakdown?.length ||
      !comparisonGroup?.breakdown?.length
    ) {
      return `There is no available data to assess the skills of workers in ${subjectClause} in ${targetGeo}`;
    }

    const targetBySkill = new Map(
      targetGroup.breakdown.map((b) => [b.dim, b.share ?? b.value ?? 0])
    );
    const compBySkill = new Map(
      comparisonGroup.breakdown.map((b) => [b.dim, b.share ?? b.value ?? 0])
    );

    let topSkill = '';
    let topShare = -Infinity;
    for (const [skill, share] of targetBySkill) {
      if (share > topShare) {
        topShare = share;
        topSkill = skill;
      }
    }

    let deficitSkill = '';
    let deficitValue = 0;
    let surplusSkill = '';
    let surplusValue = 0;
    for (const [skill, targetShare] of targetBySkill) {
      const compShare = compBySkill.get(skill) ?? 0;
      const diff = targetShare - compShare;
      if (diff < deficitValue) {
        deficitValue = diff;
        deficitSkill = skill;
      }
      if (diff > surplusValue) {
        surplusValue = diff;
        surplusSkill = skill;
      }
    }

    const fmtPct = (v: number) => {
      // Skill shares arrive as decimal fractions (0–1); scale to display percent.
      const abs = Math.abs(v);
      const pct = abs > 1 ? abs : abs * 100;
      return `${pct.toFixed(1)}%`;
    };

    let text = `In ${targetGeo}, the most prevalent skill among workers in ${subjectClause} is ${topSkill}, comprising ${fmtPct(topShare)} of the workforce.`;

    if (deficitSkill) {
      text += ` However, when compared to ${comparisonGeoLabel}, the proficiency in ${deficitSkill} among workers in ${subjectClause} is lower in ${targetGeo}, showing a deficit of ${fmtPct(deficitValue)}.`;
    }
    if (surplusSkill) {
      text += ` On the other hand, ${targetGeo} demonstrates a ${fmtPct(surplusValue)} higher prevalence of ${surplusSkill} skills.`;
    }

    return wrapHelperOutput(text);
  });

  return hbs;
};
